import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import LogoImage from '../ui/LogoImage';

const SLIDES = [
  {
    img:    '/hero/hero-1.jpg',
    label:  'In the Name of Allah',
    arabic: 'بسم الله الرحمن الرحيم',
    caption:'Welcome to Jamuiyat Nasir Al-Fridaous Al Tijaniyyah',
    cta:    { text: 'Explore Gallery',   to: '/gallery'  },
    ctaAlt: { text: 'Upcoming Events', to: '/events' },
    bg:     'from-[#05053a] to-[#0c1a4a]',
  },
  {
    img:    '/hero/hero-2.jpg',
    label:  'The Tijaniyyah Way',
    arabic: 'كفى بالله وليا وكفى بالله نصيرا',
    caption:'Following the path of Shaykh Ahmad Tijani (R.A) and the Fayda of Shaykh Ibrahim Niasse',
    cta:    { text: 'About Us',       to: '/about'  },
    ctaAlt: { text: 'Murshid Quotes', to: '/quotes' },
    bg:     'from-[#0d0d50] to-[#0a2a4a]',
  },
  {
    img:    '/hero/hero-3.jpg',
    label:  'Our Sawia Branches',
    arabic: 'الزاوية التجانية',
    caption:'Find your local Sawia centres of worship, learning and JAMA',
    cta:    { text: 'Find a Sawia', to: '/sawia'   },
    ctaAlt: { text: 'Contact Us',   to: '/contact' },
    bg:     'from-[#07072e] to-[#0a1a3a]',
  },
  {
    img:    '/hero/hero-4.jpg',
    label:  'AHL-LAH HASSANA',
    arabic: 'الله حسنة',
    caption:'Become a member of JNF and connect with a community of seekers',
    cta:    { text: 'Register Now', to: '/member/register' },
    ctaAlt: { text: 'Listen Live',  to: '/radio'           },
    bg:     'from-[#0d0d50] to-[#0f3460]',
  },
];

const INTERVAL = 6000;

export default function HeroCarousel() {
  const [current, setCurrent]     = useState(0);
  const [paused, setPaused]       = useState(false);
  const [imgLoaded, setImgLoaded] = useState({});
  const timer    = useRef(null);
  const touchX   = useRef(null);

  const goTo = useCallback((i) => setCurrent(i), []);
  const next = useCallback(() => goTo((current + 1) % SLIDES.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + SLIDES.length) % SLIDES.length), [current, goTo]);

  useEffect(() => {
    if (paused) return;
    timer.current = setInterval(next, INTERVAL);
    return () => clearInterval(timer.current);
  }, [next, paused]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'ArrowRight') next(); if (e.key === 'ArrowLeft') prev(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [next, prev]);

  const slide = SLIDES[current];

  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ height: 'clamp(520px, 88vh, 840px)' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (!touchX.current) return;
        const d = touchX.current - e.changedTouches[0].clientX;
        if (Math.abs(d) > 50) d > 0 ? next() : prev();
        touchX.current = null;
      }}
    >
      {/* ── Backgrounds ─────────────────────────────────── */}
      {SLIDES.map((s, i) => (
        <div
          key={i}
          aria-hidden={i !== current}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Gradient always behind */}
          <div className={`absolute inset-0 bg-gradient-to-br ${s.bg}`} />

          {/* Subtle pattern when no image */}
          {!imgLoaded[i] && (
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `radial-gradient(circle at 20% 50%, rgba(30,181,232,0.08) 0%, transparent 50%),
                                  radial-gradient(circle at 80% 20%, rgba(26,26,110,0.4) 0%, transparent 50%)`,
              }}
            />
          )}

          {/* Photo */}
          <img
            src={s.img}
            alt={s.label}
            loading={i === 0 ? 'eager' : 'lazy'}
            onLoad={() => setImgLoaded(p => ({ ...p, [i]: true }))}
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ${
              imgLoaded[i] ? 'opacity-100' : 'opacity-0'
            }`}
          />

          {/* Darken image for readability */}
          {imgLoaded[i] && (
            <div className="absolute inset-0 bg-gradient-to-r from-navy-900/85 via-navy-900/55 to-transparent" />
          )}
        </div>
      ))}

      {/* ── MAIN CONTENT — left column, vertically centred ─ */}
      <div className="absolute inset-0 z-20 flex items-center">
        <div className="w-full max-w-2xl px-8 sm:px-12 lg:px-16">

          {/* Logo row */}
          <div className="flex items-center gap-3 mb-7">
            <LogoImage
              size={48}
              className="rounded-full border-2 border-cyan-400 shadow-lg flex-shrink-0"
            />
            <div>
              <p className="text-white font-bold text-sm">Jamuiyat Nasir Al-Fridaous Al Tijaniyyah</p>
              <p className="text-cyan-400 text-xs">Kafa Billahi Waliyan Wakafa Billahi Nasiran</p>
            </div>
          </div>

          {/* Label badge */}
          <div key={`l-${current}`} className="animate-slide-up mb-4">
            <span className="inline-flex items-center gap-2 bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
              {slide.label}
            </span>
          </div>

          {/* Arabic — left aligned, RTL, same column as everything else */}
          <div key={`a-${current}`} className="animate-slide-up mb-2" style={{ animationDelay: '0.1s' }}>
            <p
              style={{
                fontFamily: "'Amiri', 'Scheherazade New', serif",
                fontSize: 'clamp(1.5rem, 4vw, 2.6rem)',
                lineHeight: 2,
                direction: 'rtl',
                color: '#ffffff',
                textShadow: '0 2px 12px rgba(0,0,0,0.5)',
              }}
            >
              {slide.arabic}
            </p>
          </div>

          {/* Caption */}
          <div key={`c-${current}`} className="animate-slide-up mb-8" style={{ animationDelay: '0.2s' }}>
            <p className="text-white/70 leading-relaxed text-sm sm:text-base">
              {slide.caption}
            </p>
          </div>

          {/* CTA buttons */}
          <div key={`b-${current}`} className="flex flex-wrap gap-3 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <Link
              to={slide.cta.to}
              className="bg-cyan-400 hover:bg-cyan-300 text-navy-900 font-bold px-6 py-3 rounded-xl text-sm transition-all shadow-lg hover:-translate-y-0.5 active:scale-95"
            >
              {slide.cta.text}
            </Link>
            <Link
              to={slide.ctaAlt.to}
              className="bg-white/10 hover:bg-white/20 border border-white/25 text-white font-semibold px-6 py-3 rounded-xl text-sm transition-all hover:-translate-y-0.5 active:scale-95"
            >
              {slide.ctaAlt.text}
            </Link>
          </div>
        </div>
      </div>

      {/* ── Slide counter — top right ────────────────────── */}
      <div className="absolute top-5 right-5 z-30 text-white/40 text-xs font-mono tracking-widest select-none">
        {String(current + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
      </div>

      {/* ── Bottom controls row ──────────────────────────── */}
      {/* Prev · Dots · Next — all in one centred flex row */}
      <div className="absolute bottom-6 left-0 right-0 z-30 flex items-center justify-center gap-4">

        {/* Prev button */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          className="bg-black/40 hover:bg-black/70 text-white p-2.5 rounded-full border border-white/20 transition-all hover:scale-110 active:scale-95 flex-shrink-0"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        {/* Dot indicators */}
        <div className="flex items-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-7 h-2.5 bg-cyan-400'
                  : 'w-2.5 h-2.5 bg-white/35 hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={next}
          aria-label="Next slide"
          className="bg-black/40 hover:bg-black/70 text-white p-2.5 rounded-full border border-white/20 transition-all hover:scale-110 active:scale-95 flex-shrink-0"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>

      </div>

      {/* ── Progress bar ─────────────────────────────────── */}
      {!paused && (
        <div className="absolute bottom-0 left-0 right-0 z-30 h-0.5 bg-white/10">
          <div
            key={`p-${current}`}
            className="h-full bg-cyan-400"
            style={{ animation: `hprog ${INTERVAL}ms linear forwards` }}
          />
        </div>
      )}

      <style>{`
        @keyframes hprog { from { width: 0% } to { width: 100% } }
      `}</style>
    </div>
  );
}
